import React, { useState, useRef } from 'react';
import { extractTextFromImage } from '../services/claudeApi';
import { saveCustomText } from '../utils/storage';
import { trackEvent } from '../utils/analytics';

export default function PhotoTextInput({ onTextExtracted, onCancel }) {
  const [phase, setPhase] = useState('select'); // select | loading | confirm | error
  const [pages, setPages] = useState([]); // [{ previewUrl, text }]
  const [loadingPreview, setLoadingPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const isPdfFile = file.type === 'application/pdf';
    const fileType = isPdfFile ? 'pdf' : 'image';
    const url = isPdfFile ? null : URL.createObjectURL(file);
    setLoadingPreview(url);
    setPhase('loading');

    trackEvent('ondoku_photo_ocr_started', { file_type: fileType });

    try {
      const base64 = await fileToBase64(file);
      const mediaType = file.type || 'image/jpeg';
      const text = await extractTextFromImage(base64, mediaType);
      if (!text) throw new Error('テキストを　みつけられませんでした');
      const newPages = [...pages, { previewUrl: url, text, isPdf: isPdfFile }];
      setPages(newPages);
      setPhase('confirm');
      trackEvent('ondoku_photo_ocr_complete', { file_type: fileType, page_count: newPages.length });
    } catch (e) {
      setErrorMsg(e.message || 'エラーが　おきました');
      setPhase('error');
      trackEvent('ondoku_photo_ocr_error', { file_type: fileType });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handlePageTextChange = (index, value) => {
    setPages((prev) => prev.map((p, i) => i === index ? { ...p, text: value } : p));
  };

  const handleRemovePage = (index) => {
    setPages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPage = () => {
    fileRef.current?.click();
  };

  const handleConfirm = () => {
    const combined = pages.map((p) => p.text.trim()).join('\n');
    const lines = combined
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const paragraphs = [];
    for (let i = 0; i < lines.length; i += 3) {
      paragraphs.push(lines.slice(i, i + 3));
    }
    const textTitle = title.trim() || '写真のテキスト';
    const saved = saveCustomText({ title: textTitle, author: '', paragraphs });
    trackEvent('ondoku_photo_text_saved', {
      page_count: pages.length,
      has_custom_title: title.trim().length > 0,
    });
    onTextExtracted(saved);
  };

  return (
    <div className="photo-input-screen">
      <div className="photo-input-header">
        <h3>📷 しゃしん・PDFから　よみこむ</h3>
        <p className="photo-input-sub">きょうかしょや　プリントの　しゃしんまたはPDFを　えらんでね</p>
      </div>

      {/* 隠しファイル選択 */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {phase === 'select' && (
        <div
          className="drop-zone"
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="drop-zone-icon">📖</div>
          <p>ここを　おして　しゃしん・PDFを　えらぶ</p>
          <p className="drop-zone-sub">または　ここに　ドラッグ</p>
        </div>
      )}

      {phase === 'loading' && (
        <div className="loading-box">
          {loadingPreview
            ? <img src={loadingPreview} alt="preview" className="photo-preview" />
            : <div className="pdf-loading-icon">📄</div>
          }
          <div className="spinner" />
          <p>もじを　よみとっています…</p>
        </div>
      )}

      {phase === 'confirm' && (
        <div className="confirm-box">
          <p className="confirm-label">📖 タイトル：</p>
          <input
            className="title-input"
            type="text"
            placeholder="かぜのゆうびんやさん　など"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          {pages.map((page, index) => (
            <div key={index} className="page-block">
              <div className="page-block-header">
                <span className="page-label">{page.isPdf ? '📄 PDFのテキスト' : `📷 ${index + 1}ページめ`}</span>
                {pages.length > 1 && (
                  <button className="btn-remove-page" onClick={() => handleRemovePage(index)}>✕</button>
                )}
              </div>
              <textarea
                className="extracted-text-area"
                value={page.text}
                onChange={(e) => handlePageTextChange(index, e.target.value)}
                rows={6}
              />
            </div>
          ))}

          <p className="confirm-hint">文字の読み違いがあれば　なおしてね。<br/>明るい場所で正面から撮ると　精度が上がるよ 📷</p>

          <button className="btn-add-page" onClick={handleAddPage}>
            ＋ つぎのページを　よみこむ
          </button>

          <div className="confirm-actions">
            <button className="btn-retry" onClick={() => { setPhase('select'); setPages([]); }}>
              🔄 はじめから
            </button>
            <button className="btn-next" onClick={handleConfirm} disabled={pages.length === 0}>
              ✅ これで　よむ！
            </button>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div className="error-box">
          <p className="error-icon">😢</p>
          <p>{errorMsg}</p>
          <button className="btn-retry" onClick={() => setPhase(pages.length > 0 ? 'confirm' : 'select')}>
            🔄 もういちど
          </button>
        </div>
      )}

      <button className="btn-back" onClick={onCancel} style={{ marginTop: 16 }}>
        ← もどる
      </button>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
